import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Ditado } from './Ditado';
import { useIsFocused, useRouter } from 'expo-router';
import { Alert, AppState, Platform, Pressable, Text, View } from 'react-native';
import type { AbaId, Block, Box, Item, Quadro, Traco } from '../store/types';
import { updateBox, removeBox, moveBox, boxToTask, novoBloco, archiveBox, moverItemCaixa, moverItensPara, moverBlocosPara, fimDoGrupo, grupoDosBlocos, ordenarCaixas, duplicarBox } from '../store/anotacoes';
import { getDoc, useDoc } from '../store/store';
import { setAberto, useRecolherTudo } from '../lib/recolher';
import { useSelecao } from './Selecao';
import { Ordenavel, ZonaSoltar, reordenarParte, semArrastar, seNaoSegurou } from './Arrastar';
import { excluirPequeno, inserirEm } from './ItemDeslizar';
import { CampoToque, type CampoToqueRef } from './CampoToque';
import { BlocoDeslizar } from './BlocoDeslizar';
import { aoSairDaCaixa, tocouEm } from '../lib/caixaAtiva';
import { uid } from '../lib/date';
import { ABAS, C, alpha, aba, useCores } from './theme';
import { ActionSheet, type Action } from './ui';
import { useTaskSheet } from './TaskSheet';
import { AcoesItem, acoesDoMenu, useAcoes } from './Acoes';
import { paraLixeira } from '../lib/lixeira';
import { DestinoEstudos } from './DestinoEstudos';
import { DesenhoSheet, DesenhoView } from './Desenho';
import { OrganizarIA } from './OrganizarIA';
import { iaPronta } from '../lib/ia';
import { t } from '../lib/i18n';
import { useCaixas } from '../lib/caixas';

const nomeBloco = (bl: Block) => (bl.type === 'check' ? t('Checklist') : bl.type === 'list' ? t('Lista') : bl.type === 'text' ? t('Texto livre') : bl.type === 'sub' ? t('Subtítulo') : t('Desenho'));
/** Texto 27 (A1/A2): resumo de um conjunto de blocos ("3 de 8 feitos", "5 itens" ou a 1ª linha do texto). */
function resumoDe(bls: Block[]): string {
  const ch = bls.filter(b => b.type === 'check').flatMap(b => b.items).filter(i => i.text.trim());
  if (ch.length) return t('{a} de {n} feitos', { a: ch.filter(i => i.done).length, n: ch.length });
  const li = bls.filter(b => b.type === 'list').flatMap(b => b.items).filter(i => i.text.trim());
  if (li.length) return t('{n} item(ns)', { n: li.length });
  return bls.find(b => b.type === 'text' && b.text.trim())?.text.trim().split('\n')[0].slice(0, 40) ?? '';
}
const resumoBloco = (bl: Block) => (bl.type === 'text' ? bl.text.split('\n').find(l => l.trim())?.trim() ?? '' : bl.type === 'desenho' ? '' : bl.items.find(i => i.text.trim())?.text.trim() ?? '');
const temConteudoBloco = (bl: Block) => (bl.type === 'text' || bl.type === 'sub' ? !!bl.text.trim() : bl.type === 'desenho' ? (bl.tracos?.length ?? 0) > 0 || !!bl.quadro : bl.items.some(i => i.text.trim()));

/** Texto 28 (B4) e texto 29 (4b, REGRAS 2.4): ao sair da caixa, item ou bloco VAZIO some sozinho.
 *  CAUSA de "os Item vazios continuam na caixa, mesmo fechando o app": a limpeza só tirava BLOCOS inteiros vazios.
 *  Um checklist com itens escritos e alguns "Item" em branco (criados com Enter ou pelo botão) não era um bloco vazio,
 *  então nada saía; e ela só rodava ao trocar de aba ou fechar a tela, nunca ao tocar fora da caixa nem ao fechar o app.
 *  Agora saem também os itens em branco (nunca um item com texto, nem um já marcado). Uma caixa toda vazia fica com
 *  um bloco só (para continuar escrevendo nela). */
export function limparBlocosVazios(id: string) {
  const b = getDoc('anotacoes').boxes.find(x => x.id === id);
  if (!b || !b.blocks.length) return;
  const limpo = b.blocks.map(bl => (bl.type === 'check' || bl.type === 'list' ? { ...bl, items: bl.items.filter(i => i.text.trim() || i.done) } : bl));
  const sobra = limpo.filter(bl => (bl.type === 'sub' ? !!bl.text.trim() : bl.type === 'check' || bl.type === 'list' ? bl.items.length > 0 : temConteudoBloco(bl)));
  let nova = sobra;
  if (!sobra.length) {
    const p = b.blocks.find(bl => bl.type !== 'sub') ?? b.blocks[0];
    nova = [p.type === 'check' || p.type === 'list' ? { ...p, items: p.items.slice(0, 1).map(i => ({ ...i, text: '', done: false })) } : p];
  }
  if (JSON.stringify(nova) === JSON.stringify(b.blocks)) return;
  updateBox(id, x => ({ ...x, blocks: nova }));
}
/** Texto 29 (4b): os vazios que já existiam somem na primeira abertura depois da atualização (e ao fechar o app). */
export function limparTodasAsCaixas() {
  for (const b of getDoc('anotacoes').boxes) limparBlocosVazios(b.id);
}

/** Exclui a caixa guardando uma cópia na lixeira (30 dias). */
export function excluirCaixa(box: Box) {
  paraLixeira(box.aba, 'caixa', box.title || t('Sem título'), box);
  removeBox(box.id);
}
/** Pergunta antes de excluir a caixa (vai para a lixeira). */
export function confirmarExclusao(box: Box, depois?: () => void) {
  Alert.alert(t('Excluir a caixa?'), [box.title || t('Sem título'), t('Fica 30 dias na lixeira.')].join('\n\n'), [
    { text: t('Cancelar'), style: 'cancel' },
    { text: t('Excluir'), style: 'destructive', onPress: () => { excluirCaixa(box); depois?.(); } },
  ]);
}

/** Onde entra um bloco novo (texto 29, item 4a/4c, REGRAS 2.5). */
type Onde = { k: 'barra' } | { k: 'grupo'; g: string } | { k: 'bloco'; id: string } | { k: 'item'; bl: string; it: string };
type TipoNovo = 'check' | 'list' | 'text' | 'sub' | 'desenho';
/** Coloca nb na lista de blocos no lugar pedido. "item": logo abaixo daquele item (o bloco dele se divide em dois). */
function colocarBloco(bs: Block[], onde: Onde, nb: Block, ultimoSub: string | null): Block[] {
  if (onde.k === 'item') {
    const i = bs.findIndex(b => b.id === onde.bl);
    if (i < 0) return [...bs, nb];
    const bl = bs[i], p = bl.items.findIndex(x => x.id === onde.it);
    const antes = bl.items.slice(0, p + 1), depois = bl.items.slice(p + 1);
    return [...bs.slice(0, i), { ...bl, items: antes }, nb, ...(depois.length ? [{ ...bl, id: uid(), items: depois }] : []), ...bs.slice(i + 1)];
  }
  const pos = onde.k === 'bloco' ? bs.findIndex(b => b.id === onde.id) + 1
    : onde.k === 'grupo' ? fimDoGrupo(bs, onde.g) + 1
    : ultimoSub && bs.some(b => b.id === ultimoSub) ? fimDoGrupo(bs, ultimoSub) + 1 : bs.length;
  return inserirEm(bs, pos <= 0 ? bs.length : pos, nb);
}

/** Caixa de anotações com blocos de checklist, lista e texto livre. */
/** embutida: só os blocos e os botões de tipo, sem título e sem moldura (usada dentro da Lista rápida, 0.47). */
export function BoxCard({ box, destaque, alca, embutida, fixar, onArquivar, onSelecionar }: { box: Box; destaque?: boolean; alca?: object; embutida?: boolean;
  fixar?: { on: boolean; alternar: () => void }; onArquivar?: () => void;
  /** texto 29 (REGRAS 1.9): ⋮ › Selecionar da caixa (entra na seleção das caixas) */ onSelecionar?: () => void }) {
  const { corDe, liberada } = useCores();
  const m = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const router = useRouter();
  const sheet = useTaskSheet();
  const acoes = useAcoes();
  const cor = corDe(box.aba);
  const [menu, setMenu] = useState<Action[] | null>(null);
  const [doMenu, setDoMenu] = useState(false);   // true: menu ⋮; false: "Enviar para"
  const [menuTitulo, setMenuTitulo] = useState<string | null>(null);   // texto 26: ⋮ de um bloco
  const [paraEstudos, setParaEstudos] = useState(false);
  const [organizar, setOrganizar] = useState(false);
  const refs = useRef<Record<string, CampoToqueRef | null>>({});
  const [desenho, setDesenho] = useState<{ id: string | null; tracos: Traco[]; alt: number; larg?: number; quadro?: Quadro | null; onde?: Onde } | null>(null);

  const setBlocks = (fn: (bs: Block[]) => Block[]) => updateBox(box.id, b => ({ ...b, blocks: fn(b.blocks) }));
  // texto 29 (4c): o último subtítulo em que a pessoa tocou ou escreveu (a barra de baixo põe o bloco novo no fim dele)
  const ultimoSub = useRef<string | null>(null);
  // texto 29 (4c): o bloco (ou item) recém-criado abre já para escrever
  const [novoId, setNovoId] = useState<string | null>(null);
  useEffect(() => { if (!novoId) return; const tm = setTimeout(() => setNovoId(null), 1500); return () => clearTimeout(tm); }, [novoId]);
  // texto 29 (4e): blocos em edição (cursor piscando): não deslizam nem são pegos
  const [edicao, setEdicao] = useState<Record<string, boolean>>({});
  const edicaoRef = useRef(edicao); edicaoRef.current = edicao;
  const editar = (id: string, on: boolean) => setEdicao(e => (!!e[id] === on ? e : { ...e, [id]: on }));
  // texto 29 (4e): o toque começou na faixa de cima do Desenho (ou no cabeçalho de um bloco)?
  const faixa = useRef<{ id: string; em: number } | null>(null);
  const tocouFaixa = (id: string) => ({ onTouchStart: () => { faixa.current = { id, em: Date.now() }; }, onPointerDown: () => { faixa.current = { id, em: Date.now() }; } });
  const naFaixa = (id: string) => !!faixa.current && faixa.current.id === id && Date.now() - faixa.current.em < 1500;

  // texto 28 (B4) / 29 (4b): saiu da aba, fechou a tela, tocou fora da caixa ou o app foi para o fundo: os vazios somem
  const foco = useIsFocused();
  useEffect(() => { if (!foco) { limparBlocosVazios(box.id); ultimoSub.current = null; } }, [foco, box.id]);
  useEffect(() => () => limparBlocosVazios(box.id), [box.id]);
  useEffect(() => aoSairDaCaixa(box.id, () => { limparBlocosVazios(box.id); ultimoSub.current = null; }), [box.id]);
  useEffect(() => { const s = AppState.addEventListener('change', st => { if (st !== 'active') limparBlocosVazios(box.id); }); return () => s.remove(); }, [box.id]);
  const setItems = (bid: string, fn: (it: Item[]) => Item[]) => setBlocks(bs => bs.map(b => (b.id === bid ? { ...b, items: fn(b.items) } : b)));
  const [recolhidos, setRecolhidos] = useState<Set<string>>(() => new Set());   // texto 26 (B4): blocos recolhidos
  // texto 27 (A1/A2): caixa e grupos (subtítulos) recolhidos; o app lembra (lib/recolher)
  const guardado = useRecolherTudo();
  const caixaAberta = embutida || (guardado[`caixa:${box.id}`] ?? true);
  const subAberto = (id: string) => guardado[`sub:${id}`] ?? true;
  const [editTitulo, setEditTitulo] = useState(false);
  const [editSub, setEditSub] = useState<string | null>(null);
  const grupos = grupoDosBlocos(box.blocks);
  const grupoDe = (blId: string) => grupos[box.blocks.findIndex(b => b.id === blId)] ?? '';
  const blocosDoGrupo = (sub: string) => box.blocks.filter((_, i) => grupos[i] === sub && box.blocks[i].type !== 'sub');
  const visiveis = box.blocks.filter((bl, i) => bl.type === 'sub' || !grupos[i] || subAberto(grupos[i]));
  const todasCaixas = useDoc('anotacoes').boxes;

  // ---------- seleção (texto 29, itens 1 e 2): itens de checklist/lista e blocos de Texto livre e Desenho ----------
  const todosItens = box.blocks.flatMap(bl => (bl.type === 'check' || bl.type === 'list' ? bl.items.map(it => ({ bl, it })) : []));
  const blocosSel = box.blocks.filter(bl => bl.type === 'text' || bl.type === 'desenho');
  const selecionaveis = [...todosItens.map(x => x.it.id), ...blocosSel.map(b => b.id)];
  const separar = (ids: string[]) => ({ itens: ids.filter(id => todosItens.some(x => x.it.id === id)), blocos: ids.filter(id => blocosSel.some(b => b.id === id)) });
  const sel = useSelecao({ itens: selecionaveis, chave: x => x, cor,
    onDuplicar: ids => duplicarVarios(ids),
    extras: [{ label: '↪ ' + t('Mover para…'), onPress: ids => { const s2 = separar(ids); escolherDestino(s2.itens, s2.blocos); } },
      { label: '🗑 ' + t('Excluir'), onPress: ids => excluirVarios(ids) }] });
  const descrever = (ids: string[]) => {
    const nomes = ids.map(id => todosItens.find(x => x.it.id === id)?.it.text.trim() || (() => { const b = blocosSel.find(x => x.id === id); return b ? (b.type === 'text' ? resumoBloco(b) || t('Texto livre') : t('Desenho')) : ''; })()).filter(Boolean);
    return nomes.slice(0, 4).join('\n') + (nomes.length > 4 ? '\n…' : '');
  };
  /** REGRAS 4.3 e 1.6: pergunta dizendo o que será apagado; depois, "Desfazer" */
  const excluirVarios = (ids: string[]) => excluirPequeno(true, () => {
    const antes = getDoc('anotacoes').boxes.find(b => b.id === box.id)?.blocks ?? box.blocks;
    const tirar = new Set(ids);
    setBlocks(bs => {
      const r = bs.filter(b => !tirar.has(b.id)).map(b => (b.type === 'check' || b.type === 'list' ? { ...b, items: b.items.filter(i => !tirar.has(i.id)) } : b))
        .filter(b => !(b.type === 'check' || b.type === 'list') || b.items.length);
      return r.length ? r : [novoBloco('check')];
    });
    return () => setBlocks(() => antes);
  }, ids.length === 1 ? t('Excluir 1 item?') : t('Excluir {n} itens?', { n: ids.length }), descrever(ids));
  const duplicarVarios = (ids: string[]) => {
    const quais = new Set(ids);
    setBlocks(bs => bs.flatMap(b => {
      if (quais.has(b.id)) return [b, copiaBloco(b)];
      if (b.type !== 'check' && b.type !== 'list') return [b];
      return [{ ...b, items: b.items.flatMap(i => (quais.has(i.id) ? [i, { ...i, id: uid() }] : [i])) }];
    }));
  };
  /** A3 / texto 29: "Mover para…": escolhe a caixa e, se ela tiver subtítulos, o grupo. Vale para itens e blocos. */
  const escolherDestino = (itemIds: string[], blocoIds: string[]) => {
    const itens = todosItens.filter(x => itemIds.includes(x.it.id)).map(x => ({ caixa: box.id, bloco: x.bl.id, item: x.it.id }));
    const blocos = blocoIds.map(id => ({ caixa: box.id, bloco: id }));
    const n = itens.length + blocos.length;
    if (!n) return;
    const mover = (cx: Box, sub: string) => {
      if (itens.length) moverItensPara(itens, cx.id, sub);
      if (blocos.length) moverBlocosPara(blocos, cx.id, sub);
      if (sub) setAberto(`sub:${sub}`, true);
      sel.cancelar();
    };
    const caixas = todasCaixas.filter(b => !b.archivedAt && !b.ref);
    setDoMenu(true); setMenuTitulo(n === 1 ? t('Mover para') : t('Mover {n} selecionados para', { n }));
    setMenu(caixas.map(cx => ({ label: (cx.title.trim() || t('Sem título')) + (cx.id === box.id ? ' (' + t('esta caixa') + ')' : cx.aba !== 'anotacoes' ? ' · ' + aba(cx.aba).nome : ''), onPress: () => {
      const subs = cx.blocks.filter(b => b.type === 'sub');
      if (!subs.length) return mover(cx, '');
      setTimeout(() => { setMenuTitulo(cx.title.trim() || t('Sem título')); setMenu([
        ...(cx.blocks[0]?.type === 'sub' ? [] : [{ label: t('Início da caixa'), onPress: () => mover(cx, '') }]),
        ...subs.map(sb => ({ label: '▸ ' + (sb.text.trim() || t('Subtítulo')), onPress: () => mover(cx, sb.id) }))]); }, 350);
    } })));
  };

  // ---------- acrescentar (texto 29, itens 4a e 4c, REGRAS 2.5) ----------
  const [inserir, setInserir] = useState<Onde | null>(null);
  const criar = (tipo: TipoNovo, onde: Onde) => {
    if (onde.k === 'grupo') setAberto(`sub:${onde.g}`, true);
    if (onde.k === 'barra' && ultimoSub.current) setAberto(`sub:${ultimoSub.current}`, true);
    if (tipo === 'desenho') { setDesenho({ id: null, tracos: [], alt: 500, onde }); return; }
    // no ⋮ de um item, o mesmo tipo do bloco dele vira só um item novo logo abaixo
    if (onde.k === 'item') {
      const bl = box.blocks.find(b => b.id === onde.bl);
      if (bl && bl.type === tipo) {
        const ni = { id: uid(), text: '', done: false };
        setItems(bl.id, its => inserirEm(its, its.findIndex(x => x.id === onde.it) + 1, ni));
        setNovoId(ni.id); return;
      }
    }
    const nb = novoBloco(tipo);
    const sub = ultimoSub.current;
    setBlocks(bs => colocarBloco(bs, onde, nb, sub));
    setNovoId(tipo === 'check' || tipo === 'list' ? nb.items[0].id : nb.id);
    if (tipo === 'sub') ultimoSub.current = nb.id;
  };
  const opcoesNovo = (onde: Onde): Action[] => ([['check', '☑ ' + t('Item')], ['list', '• ' + t('Lista')], ['text', t('Texto livre')], ['sub', 'H ' + t('Subtítulo')], ['desenho', '✍️ ' + t('Desenho')]] as const)
    .map(([k, l]) => ({ label: l, onPress: () => setTimeout(() => criar(k, onde), Platform.OS === 'web' ? 0 : 350) }));

  const menuItem = (bl: Block, it: Item) => { setDoMenu(true); setMenuTitulo(it.text.trim() || t('Item')); setMenu([
    { label: '↪ ' + t('Mover para…'), onPress: () => setTimeout(() => escolherDestino([it.id], []), 350) },
    { label: '＋ ' + t('Inserir abaixo'), onPress: () => setTimeout(() => setInserir({ k: 'item', bl: bl.id, it: it.id }), 350) },
    { label: '⧉ ' + t('Duplicar'), onPress: () => duplicarItem(bl, it) },
    { label: '☑ ' + t('Selecionar'), onPress: () => sel.selecionar(it.id) },
    { label: t('Excluir'), destructive: true, onPress: () => excluirItem(bl, it) },
  ]); };
  /** REGRAS 4.1: item vazio sai direto; com texto pergunta; sempre com Desfazer. O último item leva o bloco junto (se houver outro). */
  const excluirItem = (bl: Block, it: Item) => excluirPequeno(!!it.text.trim(), () => {
    const atual = box.blocks.find(b => b.id === bl.id);
    if (!atual) return;
    if (atual.items.length > 1) {
      const pos = atual.items.findIndex(x => x.id === it.id);
      setItems(bl.id, its => its.filter(x => x.id !== it.id));
      return () => setItems(bl.id, its => (its.some(x => x.id === it.id) ? its : inserirEm(its, pos, it)));
    }
    if (box.blocks.length < 2) { setItems(bl.id, () => [{ ...it, text: '', done: false }]); return () => setItems(bl.id, () => [it]); }
    return tirarBloco(atual);
  });
  const duplicarItem = (bl: Block, it: Item) => setItems(bl.id, its => { const p = its.findIndex(x => x.id === it.id); return inserirEm(its, p + 1, { ...it, id: uid() }); });
  const tirarBloco = (bl: Block) => {
    const pos = box.blocks.findIndex(b => b.id === bl.id);
    setBlocks(bs => { const r = bs.filter(b => b.id !== bl.id); return r.length ? r : [novoBloco('check')]; });
    return () => setBlocks(bs => (bs.some(b => b.id === bl.id) ? bs : inserirEm(bs.filter(b => temConteudoBloco(b) || b.type === 'sub' || bs.length > 1), pos, bl)));
  };
  const excluirBloco = (bl: Block) => excluirPequeno(temConteudoBloco(bl), () => tirarBloco(bl), t('Excluir este bloco?'), resumoBloco(bl));
  const duplicarBloco = (bl: Block) => setBlocks(bs => { const i = bs.findIndex(b => b.id === bl.id); return inserirEm(bs, i + 1, copiaBloco(bl)); });
  const mexerBloco = (id: string, d: number) => setBlocks(bs => { const i = bs.findIndex(b => b.id === id), j = i + d; if (i < 0 || j < 0 || j >= bs.length) return bs; const a = [...bs]; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const virar = (bl: Block, tipo: 'sub' | 'text') => setBlocks(bs => bs.map(b => (b.id === bl.id
    ? { ...b, type: tipo, text: tipo === 'sub' ? b.text.split('\n').map(x => x.trim()).filter(Boolean).join(' ') : b.text } : b)));
  /** Texto 29 (REGRAS 1.9): ⋮ › Selecionar de um bloco ou subtítulo: entra na seleção com o que está nele */
  const selecionarBloco = (bl: Block) => {
    const ids = bl.type === 'sub' ? blocosDoGrupo(bl.id).flatMap(b => (b.type === 'check' || b.type === 'list' ? b.items.map(i => i.id) : [b.id]))
      : bl.type === 'check' || bl.type === 'list' ? bl.items.map(i => i.id) : [bl.id];
    if (bl.type === 'sub') setAberto(`sub:${bl.id}`, true);
    ids.forEach(id => sel.selecionar(id));
  };
  const menuBloco = (bl: Block) => { setDoMenu(true); setMenuTitulo(bl.type === 'sub' ? bl.text.trim() || t('Subtítulo') : nomeBloco(bl)); setMenu([
    // texto 27 (A2): converter as caixas de texto que já eram usadas como subtítulo, sem refazer a lista
    ...(bl.type === 'text' ? [{ label: 'H ' + t('Transformar em subtítulo'), onPress: () => virar(bl, 'sub') }] : []),
    ...(bl.type === 'sub' ? [{ label: '✏️ ' + t('Editar subtítulo'), onPress: () => { setAberto(`sub:${bl.id}`, true); setEditSub(bl.id); setTimeout(() => refs.current[bl.id]?.focus(), 350); } },
      { label: '¶ ' + t('Transformar em texto'), onPress: () => virar(bl, 'text') }] : []),
    // texto 29 (4e, REGRAS 3.4): Mover para… (blocos), Inserir abaixo e Selecionar
    ...(bl.type !== 'sub' ? [{ label: '↪ ' + t('Mover para…'), onPress: () => setTimeout(() => (bl.type === 'check' || bl.type === 'list' ? escolherDestinoBloco(bl) : escolherDestino([], [bl.id])), 350) }] : []),
    { label: '＋ ' + t('Inserir abaixo'), onPress: () => setTimeout(() => setInserir(bl.type === 'sub' ? { k: 'grupo', g: bl.id } : { k: 'bloco', id: bl.id }), 350) },
    { label: '⧉ ' + t('Duplicar'), onPress: () => duplicarBloco(bl) },
    { label: '↑ ' + t('Subir'), onPress: () => mexerBloco(bl.id, -1) },
    { label: '↓ ' + t('Descer'), onPress: () => mexerBloco(bl.id, 1) },
    { label: '☑ ' + t('Selecionar'), onPress: () => selecionarBloco(bl) },
    { label: t('Excluir'), destructive: true, onPress: () => excluirBloco(bl) },
  ]); };
  /** Checklist e Lista inteiros: vão como bloco (com todos os itens) para o fim do grupo escolhido. */
  const escolherDestinoBloco = (bl: Block) => {
    const caixas = todasCaixas.filter(b => !b.archivedAt && !b.ref);
    const mover = (cx: Box, sub: string) => { moverBlocosPara([{ caixa: box.id, bloco: bl.id }], cx.id, sub); if (sub) setAberto(`sub:${sub}`, true); };
    setDoMenu(true); setMenuTitulo(t('Mover para'));
    setMenu(caixas.map(cx => ({ label: (cx.title.trim() || t('Sem título')) + (cx.id === box.id ? ' (' + t('esta caixa') + ')' : cx.aba !== 'anotacoes' ? ' · ' + aba(cx.aba).nome : ''), onPress: () => {
      const subs = cx.blocks.filter(b => b.type === 'sub');
      if (!subs.length) return mover(cx, '');
      setTimeout(() => { setMenuTitulo(cx.title.trim() || t('Sem título')); setMenu([
        ...(cx.blocks[0]?.type === 'sub' ? [] : [{ label: t('Início da caixa'), onPress: () => mover(cx, '') }]),
        ...subs.map(sb => ({ label: '▸ ' + (sb.text.trim() || t('Subtítulo')), onPress: () => mover(cx, sb.id) }))]); }, 350);
    } })));
  };

  function enviar() {
    const a: Action[] = [{ label: '📅 ' + t('My Planner (vira tarefa)'), onPress: () =>
      sheet.open({ prefill: boxToTask(box), onSaved: () => removeBox(box.id) }) }];
    for (const x of ABAS.filter(x => ['casa', 'estudos', 'bemestar', 'financas'].includes(x.id) && x.id !== box.aba))
      a.push({ label: `${x.icon} ${x.nome}${liberada(x.id) ? '' : ' 🔒'}`, onPress: () => {
        if (!liberada(x.id)) return Alert.alert(t('{aba} está bloqueada', { aba: x.nome }), t('Assine a aba em Ajustes › Compras para enviar anotações para ela.'));
        if (x.id === 'estudos') return setParaEstudos(true);
        moveBox(box.id, x.id); Alert.alert(t('Enviada'), t('A caixa está em {aba}, em "Anotações recebidas".', { aba: x.nome }));
      } });
    a.push({ label: '🤖 ' + t('Organizar com IA (vira tarefas)'), onPress: async () => {
      if (!(await iaPronta())) return Alert.alert(t('IA não ligada'), t('Ligue a sua IA em Ajustes › Inteligência artificial.'));
      setOrganizar(true);
    } });
    if (box.aba !== 'anotacoes') a.push({ label: '🗒️ ' + t('Voltar para Anotações'), onPress: () => moveBox(box.id, 'anotacoes') });
    a.push({ label: '📤 ' + t('Outra aba…'), onPress: () => acoes.enviar({ titulo: box.title.trim() || t('Anotação'), origem: box.aba,
      itens: box.blocks.flatMap(bl => (bl.type === 'text' ? [] : bl.items.map(i => i.text))),
      texto: box.blocks.filter(bl => bl.type === 'text').map(bl => bl.text.trim()).filter(Boolean).join('\n\n'), remover: () => removeBox(box.id) }) });
    setDoMenu(false); setMenuTitulo(null); setMenu(a);
  }
  /** Menu ⋮ da caixa: Fixar no topo, Arquivar, Duplicar e Apagar. */
  function opcoes() {
    setDoMenu(true); setMenuTitulo(null); setMenu([
      ...acoesDoMenu(acoes.enviar, { fixar, lembrar: { aba: box.aba, id: box.id, titulo: box.title || t('Sem título') } }),
      { label: '✏️ ' + t('Renomear'), onPress: () => { setAberto(`caixa:${box.id}`, true); setEditTitulo(true); } },
      ...(onArquivar ? [{ label: '📦 ' + t('Arquivar'), onPress: onArquivar }] : []),
      { label: '⧉ ' + t('Duplicar'), onPress: () => duplicarBox(box.id) },
      ...(onSelecionar ? [{ label: '☑ ' + t('Selecionar'), onPress: onSelecionar }] : []),
      { label: t('Apagar caixa'), destructive: true, onPress: () => confirmarExclusao(box) },
    ]);
  }
  /** O ⋮ pequeno (blocos, itens e subtítulos) */
  const Pontos = ({ onPress, rotulo, tam = 18 }: { onPress: () => void; rotulo: string; tam?: number }) => (
    <View {...semArrastar}>
      <Pressable onPress={onPress} hitSlop={10} accessibilityLabel={rotulo}><Text style={{ color: C.sub, fontSize: tam, fontWeight: '800', paddingHorizontal: 4 }}>⋮</Text></Pressable>
    </View>
  );
  const blocosNaoSub = box.blocks.filter(b => b.type !== 'sub').length;
  const temSub = box.blocks.some(b => b.type === 'sub');

  return (
    <View onStartShouldSetResponderCapture={() => { tocouEm(box.id); return false; }}>
    {/* texto 29 (4e): soltar um bloco (Texto livre, Lista, Desenho) em cima de outra caixa leva o bloco para o fim dela */}
    <ZonaSoltar grupo="anot-blocos" id={box.id} corDestaque={cor} style={{ borderRadius: m.raio(18) }}>
    <ZonaSoltar grupo="anot-itens" id={`box:${box.id}`} corDestaque={cor} toque={embutida ? undefined : alca} style={embutida ? { gap: m.gap(8) } : { backgroundColor: C.card, borderRadius: m.raio(18), ...m.pads(14), gap: m.gap(8), borderLeftWidth: 4, borderLeftColor: cor,
      borderWidth: destaque ? 1 : 0, borderColor: cor }}>
      {/* texto 27 (A1): tocar no TÍTULO recolhe a caixa ("▸ Título · 0 de 14 feitos"); renomear fica no ⋮ (caixa sem título: escreve direto) */}
      {!embutida && <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        {!box.title.trim() || editTitulo ? (
          // texto 29 (4d): o campo fica aberto enquanto a pessoa escreve (só fecha com Enter, tocando fora ou saindo)
          <View {...(editTitulo ? semArrastar : {})} style={{ flex: 1, minWidth: 0 }}>
            <CampoToque value={box.title} onChangeText={v => updateBox(box.id, b => ({ ...b, title: v }))} placeholder={t('Título da caixa')}
              comecarEditando={editTitulo} onEditando={setEditTitulo} returnKeyType="done"
              style={{ flex: 1, minWidth: 0, color: C.text, fontSize: 17, fontWeight: '700', padding: 0 }} />
          </View>
        ) : (
          // texto 28 (B5, REGRAS 5.2a): recolhida, o título aparece inteiro (até 2 linhas) e o resumo fica embaixo
          <Pressable onPress={seNaoSegurou(() => { if (caixaAberta) limparBlocosVazios(box.id); setAberto(`caixa:${box.id}`, !caixaAberta); })} accessibilityRole="button" accessibilityState={{ expanded: caixaAberta }}
            style={{ flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'flex-start', gap: 6 }}>
            <Text style={{ color: C.mute, fontSize: 13, marginTop: 4 }}>{caixaAberta ? '▾' : '▸'}</Text>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ color: C.text, fontSize: 17, fontWeight: '700' }} numberOfLines={caixaAberta ? undefined : 2}>{box.title}</Text>
              {!caixaAberta && !!resumoDe(box.blocks) && <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={1}>{resumoDe(box.blocks)}</Text>}
            </View>
          </Pressable>
        )}
        <Pressable {...semArrastar} onPress={enviar} hitSlop={8} style={{ paddingHorizontal: 10, paddingVertical: 5, borderRadius: 12, backgroundColor: alpha(cor, 0.2) }}>
          <Text style={{ color: C.text, fontSize: 12, fontWeight: '600' }}>{t('Enviar para…')} ↗</Text>
        </Pressable>
        {fixar?.on && <Text style={{ fontSize: 11 }}>⭐</Text>}
        <Pressable {...semArrastar} onPress={opcoes} hitSlop={10} accessibilityLabel={t('Mais opções')}><Text style={{ color: C.sub, fontSize: 20, fontWeight: '800', paddingHorizontal: 2 }}>⋮</Text></Pressable>
      </View>}
      {caixaAberta && !!box.link && (
        <Pressable onPress={() => router.push({ pathname: '/aba/[id]', params: { id: box.link!.aba, ...(box.link!.id ? { item: box.link!.id } : {}) } } as any)}
          style={{ alignSelf: 'flex-start', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 12, backgroundColor: alpha(corDe(box.link.aba), 0.18) }}>
          <Text style={{ color: C.text, fontSize: 12, fontWeight: '700' }}>{aba(box.link.aba).icon} {t('Abrir em {aba}', { aba: aba(box.link.aba).nome })} ›</Text>
        </Pressable>
      )}

      {caixaAberta && sel.barra}
      {caixaAberta && <Ordenavel itens={visiveis} chave={bl => bl.id} alca alcaImediata gap={4} onOrdem={novos => setBlocks(bs => reordenarParte(bs, novos, b => b.id))}
        // texto 29 (4e): segurar e arrastar um bloco até outra caixa: ele entra no fim dela (subtítulos ficam onde estão)
        grupo="anot-blocos" listaId={box.id}
        onMover={(bl, destino) => {
          if (bl.type === 'sub' || destino === box.id) return;
          const alvo = getDoc('anotacoes').boxes.find(b => b.id === destino);
          if (!alvo) return;
          moverBlocosPara([{ caixa: box.id, bloco: bl.id }], alvo.id, grupoDosBlocos(alvo.blocks).at(-1) ?? '');
        }}
        selecao={sel.prop} selecionavel={bl => bl.type === 'text' || bl.type === 'desenho'}
        onToqueAlca={k => {
          const bl = box.blocks.find(b => b.id === k);
          if (bl?.type === 'sub') { ultimoSub.current = k; return setAberto(`sub:${k}`, !subAberto(k)); }   // A2 e 29 (4c)
          // no Texto livre, o toque curto no meio do bloco é para escrever (o campo cuida); só o cabeçalho recolhe
          if (!naFaixa(k)) return;
          setRecolhidos(s => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
        }} render={(bl, bi, ib) => {
        if (bl.type === 'sub') {
          // texto 27 (A2): Subtítulo: letra maior e negrito; tocar recolhe o grupo (até o próximo subtítulo), com resumo.
          // Soltar um item em cima dele (mesmo recolhido) leva o item para o fim daquele grupo (A3).
          // texto 29 (4d): CAUSA do "nome fecha na primeira letra": o campo aparecia só enquanto o nome estava VAZIO
          // (editando = ... || !texto); com a 1ª letra o nome deixava de ser vazio, o campo sumia e o texto ficava salvo.
          // Agora o campo fica aberto enquanto está em edição, e só fecha com Enter, tocando fora ou saindo da caixa.
          const ab = subAberto(bl.id), editando = editSub === bl.id || !bl.text.trim();
          const ultimo = (() => { let f = -1; grupos.forEach((g, i) => { if (g === bl.id) f = i; }); return f; })();
          return (
            <ZonaSoltar grupo="anot-itens" id={`sub:${box.id}:${bl.id}`} corDestaque={cor} style={{ borderRadius: 8 }}>
              <View {...ib.alca} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, borderTopWidth: bi ? 1 : 0, borderTopColor: C.line }}
                accessibilityLabel={ab ? t('Recolher o grupo') : t('Abrir o grupo')}>
                <Text style={{ color: C.sub, fontSize: 13 }}>{ab ? '▾' : '▸'}</Text>
                {editando ? (
                  <View {...semArrastar} style={{ flex: 1, minWidth: 0 }}>
                    <CampoToque ref={r => { refs.current[bl.id] = r; }} value={bl.text} comecarEditando={editSub === bl.id || novoId === bl.id}
                      onEditando={on => { if (on) { setEditSub(bl.id); ultimoSub.current = bl.id; } else setEditSub(s => (s === bl.id ? null : s)); }}
                      returnKeyType="done" onSubmitEditing={() => refs.current[bl.id]?.blur()}
                      onChangeText={v => setBlocks(bs => bs.map(b => (b.id === bl.id ? { ...b, text: v } : b)))} placeholder={t('Subtítulo')}
                      style={{ flex: 1, minWidth: 0, color: C.text, fontSize: 18, fontWeight: '800', padding: 0 }} />
                  </View>
                ) : (
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ color: C.text, fontSize: 18, fontWeight: '800' }} numberOfLines={ab ? undefined : 2}>{bl.text}</Text>
                    {!ab && !!resumoDe(blocosDoGrupo(bl.id)) && <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={1}>{resumoDe(blocosDoGrupo(bl.id))}</Text>}
                  </View>
                )}
                <Pontos onPress={() => menuBloco(bl)} rotulo={t('Opções do bloco')} />
              </View>
              {/* texto 29 (4a, REGRAS 2.5): grupo vazio e aberto: o "+ adicionar aqui" fica logo embaixo do subtítulo */}
              {ab && ultimo === box.blocks.findIndex(b => b.id === bl.id) && <AdicionarAqui onPress={() => setInserir({ k: 'grupo', g: bl.id })} />}
            </ZonaSoltar>
          );
        }
        // texto 29 (4a): o último bloco de um grupo aberto leva embaixo o "+ adicionar aqui"
        const g = grupos[box.blocks.findIndex(b => b.id === bl.id)] ?? '';
        const fimGrupo = !!g && subAberto(g) && (() => { let f = -1; grupos.forEach((x, i) => { if (x === g) f = i; }); return box.blocks[f]?.id === bl.id; })();
        // texto 26 (B4) / 28 (B3) / 29 (4e): com 2 ou mais blocos, Checklist e Lista têm um cabeçalho fino
        // ("▾ Checklist ⋮"): segurar ~1 s nele pega o bloco; tocar recolhe; o ⋮ tem todas as opções do bloco.
        // Texto livre: o ⋮ fica junto do 🎤. Desenho: sempre a faixa de cima ("✍️ Desenho ⋮").
        const varios = bl.type === 'desenho' || ((bl.type === 'check' || bl.type === 'list') && box.blocks.length > 1)
          || (bl.type === 'text' && !temSub && blocosNaoSub > 1);
        const recolhido = varios && recolhidos.has(bl.id);
        const emEdicao = !!edicao[bl.id];
        const ehTexto = bl.type === 'text';
        const cab = varios && (
          <View {...ib.alca} {...tocouFaixa(bl.id)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4, paddingHorizontal: 2, borderTopWidth: bi && !(box.blocks[bi - 1]?.type === 'sub') ? 1 : 0, borderTopColor: C.line }}
            accessibilityLabel={recolhido ? t('Abrir o bloco') : t('Recolher o bloco')}>
            <Text style={{ color: C.mute, fontSize: 11 }}>{recolhido ? '▸' : '▾'}</Text>
            <Text style={{ color: C.sub, fontSize: 11, fontWeight: '700', flex: 1 }} numberOfLines={1}>
              {bl.type === 'desenho' ? '✍️ ' : ''}{nomeBloco(bl)}{recolhido && resumoBloco(bl) ? ' · ' + resumoBloco(bl) : ''}</Text>
            <Pontos onPress={() => menuBloco(bl)} rotulo={t('Opções do bloco')} />
          </View>
        );
        const corpo = !recolhido && (bl.type === 'desenho' ? (
          // dentro da área do desenho, segurar não pega nada (só a faixa de cima)
          <Pressable {...semArrastar} onTouchStart={() => { faixa.current = null; }} onPress={seNaoSegurou(() => setDesenho({ id: bl.id, tracos: bl.tracos ?? [], alt: bl.alt ?? 500, larg: bl.larg, quadro: bl.quadro }))}>
            <DesenhoView tracos={bl.tracos ?? []} alt={bl.alt ?? 500} larg={bl.larg} quadro={bl.quadro} />
          </Pressable>
        ) : ehTexto ? (
          // texto 29 (4e): fora de edição, segurar ~1 s em qualquer ponto do Texto livre pega o bloco; tocar escreve
          <View {...(emEdicao ? {} : ib.alca)} style={{ flexDirection: 'row', gap: 6 }}>
            <CampoToque multiline value={bl.text} placeholder={t('Texto livre')} comecarEditando={novoId === bl.id}
              onEditando={on => { editar(bl.id, on); if (on && g) ultimoSub.current = g; }}
              onChangeText={v => setBlocks(bs => bs.map(b => (b.id === bl.id ? { ...b, text: v } : b)))}
              estiloCaixa={{ flex: 1, minWidth: 0, backgroundColor: C.card2, borderRadius: 10, ...m.padV(10), minHeight: m.alt(70), justifyContent: 'flex-start' }}
              style={{ flex: 1, minWidth: 0, color: C.text, textAlignVertical: 'top' }} />
            <View {...semArrastar} style={{ alignItems: 'center', gap: 4 }}>
              <Ditado pequeno cor={C.sub} onTexto={tx => setBlocks(bs => bs.map(b => (b.id === bl.id && b.type === 'text' ? { ...b, text: b.text ? b.text + ' ' + tx : tx } : b)))} />
              {!varios && <Pontos onPress={() => menuBloco(bl)} rotulo={t('Opções do bloco')} />}
            </View>
          </View>
        ) : (
          <Ordenavel itens={bl.items} chave={it => it.id} alca gap={0} grupo="anot-itens" listaId={`${box.id}:${bl.id}`}
            onOrdem={novos => setItems(bl.id, () => novos)} selecao={sel.prop}
            onMover={(it, destino) => { moverItemCaixa(box.id, bl.id, it.id, destino); if (destino.startsWith('sub:')) setAberto(`sub:${destino.split(':')[2]}`, true); }}
            render={(it, i, ia) => (
          // texto 28 (B1, REGRAS 2): sem deslizar para o lado; Excluir, Duplicar e "Mover para…" ficam no ⋮ do item.
          // texto 29 (itens 1 e 3): segurar ~1 s em qualquer ponto do item (também em cima do texto) pega o item; o texto
          // só vira campo com o toque. Ir para o lado em cima de um item de checklist desliza a CAIXA inteira.
          <View {...ia.alca} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingLeft: 4, backgroundColor: ia.arrastando ? C.card : 'transparent' }}>
            {bl.type === 'check' ? (
              <Pressable hitSlop={8} onPress={seNaoSegurou(() => setItems(bl.id, its => its.map(x => (x.id === it.id ? { ...x, done: !x.done } : x))))}
                style={{ width: 20, height: 20, borderRadius: 6, borderWidth: 2, borderColor: it.done ? cor : C.mute, backgroundColor: it.done ? cor : 'transparent',
                  alignItems: 'center', justifyContent: 'center' }}>{it.done && <Text style={{ color: '#fff', fontSize: 11, fontWeight: '900' }}>✓</Text>}</Pressable>
            ) : <Text style={{ color: cor, fontSize: 18, width: 20, textAlign: 'center' }}>•</Text>}
            <CampoToque ref={r => { refs.current[it.id] = r; }} value={it.text} placeholder={bl.type === 'check' ? t('Item') : t('Lista')}
              comecarEditando={novoId === it.id} blurOnSubmit={false} returnKeyType="next"
              onEditando={on => { editar(bl.id, on); if (on && g) ultimoSub.current = g; }}
              style={{ flex: 1, minWidth: 0, color: C.text, paddingVertical: m.pad(7), textDecorationLine: it.done ? 'line-through' : 'none', opacity: it.done ? 0.6 : 1 }}
              onChangeText={v => setItems(bl.id, its => its.map(x => (x.id === it.id ? { ...x, text: v } : x)))}
              onSubmitEditing={() => { const n = { id: uid(), text: '', done: false }; setItems(bl.id, its => { const a = [...its]; a.splice(i + 1, 0, n); return a; }); setNovoId(n.id); }}
              onKeyPress={e => {
                if (e.nativeEvent.key !== 'Backspace' || it.text) return;
                if (bl.items.length > 1) { const prev = bl.items[i - 1]?.id; setItems(bl.id, its => its.filter(x => x.id !== it.id)); if (prev) setTimeout(() => refs.current[prev]?.focus(), 60); }
                else if (box.blocks.length > 1) setBlocks(bs => bs.filter(b => b.id !== bl.id));
              }} />
            {/* texto 27 (A3) e 29: o ⋮ do item tem "Mover para…", Inserir abaixo, Duplicar, Selecionar e Excluir */}
            <Pontos onPress={() => menuItem(bl, it)} rotulo={t('Opções do item')} tam={16} />
          </View>
            )} />
        ));
        const conteudo = <View style={{ gap: 2 }}>{cab}{corpo}</View>;
        // texto 29 (4e, REGRAS 3.5): Texto livre, Lista e Desenho deslizam sozinhos (direita = excluir, esquerda = duplicar);
        // em edição não deslizam; o Desenho só pela faixa de cima. O Checklist não desliza: vai a caixa inteira (item 3).
        return (
          <View>
            {bl.type === 'check' ? conteudo : (
              <BlocoDeslizar pode={() => !edicaoRef.current[bl.id] && (bl.type !== 'desenho' || naFaixa(bl.id))}
                onExcluir={() => excluirBloco(bl)} onDuplicar={() => duplicarBloco(bl)}>{conteudo}</BlocoDeslizar>
            )}
            {fimGrupo && <AdicionarAqui onPress={() => setInserir({ k: 'grupo', g })} />}
          </View>
        );
      }} />}

      {caixaAberta && <View style={{ flexDirection: 'row', gap: 8, marginTop: m.gap(4), flexWrap: 'wrap' }}>
        {/* texto 29 (4c): a barra põe o bloco no fim do grupo do último subtítulo tocado (ou no fim da caixa) */}
        {([['check', '☑ ' + t('Item')], ['list', '• ' + t('Lista')], ['text', t('Texto livre')], ['sub', 'H ' + t('Subtítulo')]] as const).map(([k, l]) => (
          <Pressable key={k} {...semArrastar} onPress={() => criar(k, { k: 'barra' })} style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 12, backgroundColor: C.card2 }}>
            <Text style={{ color: C.sub, fontSize: 12 }}>{l}</Text>
          </Pressable>
        ))}
        <Pressable {...semArrastar} onPress={() => criar('desenho', { k: 'barra' })} style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 12, backgroundColor: C.card2 }}>
          <Text style={{ color: C.sub, fontSize: 12 }}>✍️ {t('Desenho')}</Text>
        </Pressable>
        {box.aba !== 'anotacoes' && !box.ref && <Text style={{ color: C.mute, fontSize: 11, alignSelf: 'center' }}>{t('de Anotações')}</Text>}
      </View>}
      {caixaAberta && box.aba !== 'anotacoes' && !box.ref && (
        <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
          <Pressable onPress={() => moveBox(box.id, 'anotacoes')} style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 12, backgroundColor: alpha(cor, 0.2) }}>
            <Text style={{ color: C.text, fontSize: 12, fontWeight: '600' }}>🗒️ {t('Voltar para Anotações')}</Text>
          </Pressable>
          <Pressable onPress={() => confirmarExclusao(box)} style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 12, backgroundColor: alpha(C.danger, 0.15) }}>
            <Text style={{ color: C.danger, fontSize: 12, fontWeight: '600' }}>🗑 {t('Excluir')}</Text>
          </Pressable>
        </View>
      )}
      <ActionSheet title={menuTitulo ?? (doMenu ? box.title || t('caixa') : t('Enviar "{titulo}" para', { titulo: box.title || t('caixa') }))} actions={menu} onClose={() => setMenu(null)} />
      <ActionSheet title={t('Acrescentar')} actions={inserir ? opcoesNovo(inserir) : null} onClose={() => setInserir(null)} />
      <DesenhoSheet visible={!!desenho} inicial={desenho?.tracos} inicialQ={desenho?.quadro} alt={desenho?.alt ?? 500} larg={desenho?.larg} cor={cor} aba="anotacoes" onClose={() => setDesenho(null)}
        onSalvar={(tracos, alt, larg, quadro) => {
          const id = desenho?.id, onde = desenho?.onde ?? { k: 'barra' as const }, sub = ultimoSub.current;
          if (id) setBlocks(bs => bs.map(b => (b.id === id ? { ...b, tracos, alt, larg, quadro } : b)));
          else if (tracos.length || quadro) setBlocks(bs => colocarBloco(bs, onde, { ...novoBloco('desenho'), tracos, alt, larg, quadro }, sub));
          setDesenho(null);
        }} />
      <OrganizarIA box={organizar ? box : null} cor={cor} onClose={() => setOrganizar(false)} />
      <DestinoEstudos box={paraEstudos ? box : null} cor={corDe('estudos')} onClose={() => setParaEstudos(false)} />
    </ZonaSoltar>
    </ZonaSoltar>
    </View>
  );
}

/** Texto 29 (4a, REGRAS 2.5): linha discreta no fim de um grupo aberto. */
function AdicionarAqui({ onPress }: { onPress: () => void }) {
  return (
    <Pressable {...semArrastar} onPress={onPress} hitSlop={6} accessibilityRole="button" style={{ alignSelf: 'flex-start', paddingVertical: 4, paddingHorizontal: 6 }}>
      <Text style={{ color: C.mute, fontSize: 12 }}>+ {t('adicionar aqui')}</Text>
    </Pressable>
  );
}

/** Cópia de um bloco (ids novos), para Duplicar. */
function copiaBloco(bl: Block): Block {
  const c: Block = JSON.parse(JSON.stringify(bl));
  c.id = uid(); c.items = c.items.map(x => ({ ...x, id: uid() }));
  return c;
}

/** Caixas que vieram de Anotações para uma aba. */
export function ReceivedBoxes({ boxes, abaId }: { boxes: Box[]; abaId: AbaId }) {
  const m = useCaixas();
  const list = boxes.filter(b => b.aba === abaId && !b.archivedAt && !b.ref);
  if (!list.length) return null;
  return (
    <View style={{ gap: m.gap(10) }}>
      <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 14 }}>{t('ANOTAÇÕES RECEBIDAS · {aba}', { aba: aba(abaId).nome.toUpperCase() })}</Text>
      <Ordenavel itens={list} chave={b => b.id} alca gap={m.gap(10)} onOrdem={ordenarCaixas} render={(b, _i, a) => <BoxCard box={b} alca={a.alca} />} />
    </View>
  );
}

/**
 * Envolve uma caixa com as ações universais (0.65, as mesmas do resto do app — AcoesItem):
 * para a direita = excluir (pede confirmação; vai para a lixeira); para a esquerda = botões Arquivar e Duplicar.
 */
export function SwipeBox({ box, children, onArquivada }: { box: Box; children: ReactNode; onArquivada?: (b: Box) => void }) {
  const m = useCaixas();
  return (
    <AcoesItem lembrar={{ aba: box.aba, id: box.id, titulo: box.title || t('Sem título') }} nome={box.title || t('Sem título')} raio={m.raio(18)} menuProprio semConfirmar onExcluir={() => confirmarExclusao(box)}
      onArquivar={() => { archiveBox(box.id); onArquivada?.(box); }} onDuplicar={() => duplicarBox(box.id)}>
      {children}
    </AcoesItem>
  );
}
